// Aviso "el servidor ha cortado la sesión del empleado" (sin dependencias:
// lo importa el interceptor de axios, que no puede depender de los slices).
export const EMPLOYEE_SESSION_ENDED_EVENT = "tmo:employee-session-ended";
const ENDED_DEBOUNCE_MS = 5000;
let lastEndedAt = -Infinity;

// 2026-10-06 · Motivos por los que la sesión del empleado se corta y el TPV
// vuelve al kiosko EN SILENCIO (aviso informativo, no error): el empleado
// entró en otro dispositivo, un responsable cerró la sesión o el empleado
// fue dado de baja. El API los manda en el 401 (`reason`) y en el evento
// device.forceSelection (`notice.reason`).
export const QUIET_END_REASONS = ["EMPLOYEE_SESSION_SUPERSEDED", "SESSION_REVOKED", "EMPLOYEE_INACTIVE"];

export function isQuietEnd(reason) {
  return QUIET_END_REASONS.includes(reason);
}

/** Texto del aviso del kiosko para un motivo (null si no hay que avisar). */
export function kioskNoticeText(notice) {
  if (!notice?.reason) return null;
  const who = notice.employeeName || "Este empleado";
  switch (notice.reason) {
    case "EMPLOYEE_SESSION_SUPERSEDED":
      return `${who} ya está siendo utilizado en otro dispositivo${notice.deviceName ? ` («${notice.deviceName}»)` : ""}. Elige tu empleado para continuar.`;
    case "SESSION_REVOKED":
      return `Se ha cerrado la sesión de ${notice.employeeName || "este empleado"}. Elige tu empleado para continuar.`;
    case "EMPLOYEE_INACTIVE":
      return `${who} ya no está activo. Elige tu empleado para continuar.`;
    default:
      return null;
  }
}

/** Aviso del evento device.forceSelection (camelCase o PascalCase), o null. */
export function forceSelectionNotice(payload) {
  const n = payload?.notice || payload?.Notice;
  if (!n) return null;
  const reason = n.reason ?? n.Reason ?? null;
  if (!reason) return null;
  return {
    reason,
    employeeName: n.employeeName ?? n.EmployeeName ?? null,
    deviceName: n.deviceName ?? n.DeviceName ?? null,
  };
}

/**
 * Lo llama el interceptor de axios tras un 401 EMPLOYEE_SESSION_EXPIRED real.
 * `detail` (opcional): { reason, employeeName, deviceName } para el aviso.
 */
export function notifyEmployeeSessionEnded(now = Date.now(), detail = null) {
  if (now - lastEndedAt < ENDED_DEBOUNCE_MS) return false;
  lastEndedAt = now;
  try {
    window.dispatchEvent(new CustomEvent(EMPLOYEE_SESSION_ENDED_EVENT, { detail }));
  } catch { /* ignore */ }
  return true;
}

export function resetEmployeeSessionEndedDebounce() {
  lastEndedAt = -Infinity;
}

// 2026-10-06 · Repetir una consulta (GET) sin la sesión del empleado tras un
// corte «tranquilo» (ver isQuietEnd). Null = no aplica.
export function quietRetryConfig(error) {
  const cfg = error?.config;
  const body = error?.response?.data || {};
  if (!cfg || cfg.__quietEndRetried) return null;
  if (!isQuietEnd(body.reason)) return null;
  if (String(cfg.method || "get").toLowerCase() !== "get") return null;
  const headers = { ...(cfg.headers || {}) };
  delete headers["X-Employee-Session"];
  delete headers["x-employee-session"];
  headers.UserEmployeeId = 0;
  return { ...cfg, headers, __quietEndRetried: true };
}

// 2026-10-18 · Tokens de empleado que el PROPIO dispositivo ha soltado a
// propósito (cambio de empleado → admin, o a un empleado sin PIN). Tras el
// logout el servidor contesta 401 EMPLOYEE_SESSION_EXPIRED a cualquier
// petición que aún viajara con ese token (las que salieron justo antes del
// cambio, o las que la propia identificación nueva dispara). Eso NO es que
// la sesión del empleado actual haya muerto: antes se trataba como tal y el
// TPV volvía al kiosko → «vuelve a pedir el PIN» tras pasarse al admin.
const RETIRED_MAX = 20;
const retiredTokens = [];

/** Marca un token como soltado voluntariamente por este dispositivo. */
export function retireEmployeeSessionToken(token) {
  if (!token || retiredTokens.includes(token)) return;
  retiredTokens.push(token);
  if (retiredTokens.length > RETIRED_MAX) retiredTokens.shift();
}

export function isRetiredEmployeeSessionToken(token) {
  return !!token && retiredTokens.includes(token);
}

export function resetRetiredEmployeeSessionTokens() {
  retiredTokens.length = 0;
}

function sentEmployeeSessionToken(config) {
  const h = config?.headers || {};
  return h["X-Employee-Session"] || h["x-employee-session"] || null;
}

/** ¿El 401 es de un token que este dispositivo ya soltó a propósito? */
export function isRetiredSessionError(error) {
  return isRetiredEmployeeSessionToken(sentEmployeeSessionToken(error?.config));
}

/**
 * Config para repetir una CONSULTA que viajó con un token soltado: sin ese
 * token y con la identidad vigente del dispositivo (el interceptor adjunta
 * la sesión actual si la hay). Las escrituras no se repiten: eran del
 * empleado anterior y no deben quedar a nombre del nuevo. Null = no aplica.
 */
export function retiredSessionRetryConfig(error, storage = window.localStorage) {
  const cfg = error?.config;
  if (!cfg || cfg.__retiredSessionRetried) return null;
  if (!isRetiredSessionError(error)) return null;
  if (String(cfg.method || "get").toLowerCase() !== "get") return null;
  const headers = { ...(cfg.headers || {}) };
  delete headers["X-Employee-Session"];
  delete headers["x-employee-session"];
  let current = 0;
  try { current = Number(storage.getItem("UserEmployeeId")) || 0; } catch { /* ignore */ }
  headers.UserEmployeeId = current;
  const next = { ...cfg, headers, __retiredSessionRetried: true };
  delete next.__employeeSessionToken; // si no, el interceptor lo volvería a poner
  return next;
}
